import type { ReactNode } from "react";
import type { TFunction } from "i18next";
import { Images } from "lucide-react";

import { fmtRelative } from "~/shared/lib/fmt";
import { cn } from "~/shared/lib/utils";
import { SECTION_V5 } from "~/shared/section";
import { Badge } from "~/shared/ui/badge";
import { MediaThumb } from "~/shared/ui/media-thumb";

import type { Element } from "../types";

interface ElementCardProps {
    t: TFunction;
    element: Element;
    /** Действия над элементом (меню «изменить / удалить»), верхний правый угол постера. */
    actionSlot?: ReactNode;
    /** Клик по карточке — открыть форму редактирования. */
    onOpen?: (element: Element) => void;
}

/** Карточка элемента библиотеки: превью первой картинки + счётчик картинок + имя/описание. */
export function ElementCard({ t, element, actionSlot, onOpen }: ElementCardProps) {
    const cover = element.images[0]?.s3Url ?? null;

    return (
        <div
            role={onOpen ? "button" : "presentation"}
            tabIndex={onOpen ? 0 : undefined}
            onClick={onOpen ? () => onOpen(element) : undefined}
            onKeyDown={
                onOpen
                    ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onOpen(element);
                          }
                      }
                    : undefined
            }
            className={cn(SECTION_V5.card, onOpen && "cursor-pointer")}
        >
            <div className={SECTION_V5.cardMedia}>
                {cover ? (
                    <MediaThumb url={cover} kind="image" className="absolute inset-0" />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                        <Images className="size-8" aria-hidden />
                    </div>
                )}
                <div className="absolute top-2 left-2 flex items-center gap-1.5">
                    <Badge variant="secondary" className="bg-black/60 font-normal text-white backdrop-blur">
                        {t("elements:card.imagesCount", { count: element.images.length })}
                    </Badge>
                </div>
                {actionSlot ? (
                    <div
                        role="presentation"
                        className="absolute top-2 right-2"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {actionSlot}
                    </div>
                ) : null}
            </div>

            <div className="flex shrink-0 flex-col gap-2 px-4 py-3">
                <div className="flex items-start gap-2">
                    <h3 className="line-clamp-2 flex-1 text-sm leading-tight font-medium">{element.name}</h3>
                    <span className="shrink-0 text-[11px] whitespace-nowrap text-muted-foreground tabular-nums">
                        {fmtRelative(element.createdAt)}
                    </span>
                </div>

                <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    <Badge variant="outline" className="font-normal">
                        {t(`elements:source.${element.source}`)}
                    </Badge>
                </div>

                {element.description ? (
                    <span className="line-clamp-2 text-xs text-muted-foreground">{element.description}</span>
                ) : null}
            </div>
        </div>
    );
}
